import { useCallback, useRef, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '300px';

/**
 * Reports whether the element behind the returned ref has come within `rootMargin` of the
 * viewport at least once. Stays true afterwards so what has loaded is not thrown away by
 * scrolling on. Without IntersectionObserver the element counts as seen, so nothing waits on a
 * viewport that can't be measured.
 */
const useHasBeenInView = <T extends HTMLElement>(rootMargin = DEFAULT_ROOT_MARGIN) => {
  const [hasBeenInView, setHasBeenInView] = useState(typeof IntersectionObserver === 'undefined');
  const observerRef = useRef<IntersectionObserver>(undefined);
  const seenRef = useRef(hasBeenInView);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = undefined;

      if (!node || seenRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some(({ isIntersecting }) => isIntersecting)) return;

          seenRef.current = true;
          observer.disconnect();
          setHasBeenInView(true);
        },
        { rootMargin }
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin]
  );

  return [ref, hasBeenInView] as const;
};

export default useHasBeenInView;
